import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { StockEntry } from "@/lib/erp";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/config/routes";

export interface UseCreateStockEntryOptions {
  /** When false, skip navigation to detail page after creation. Default: true */
  navigateOnSuccess?: boolean;
  /** Item code — used to invalidate stock bin and dashboard caches */
  itemCode?: string;
  /** Custom success callback (only called when navigateOnSuccess is false) */
  onSuccess?: (data: StockEntry) => void;
}

/**
 * Hook for creating a new stock entry
 *
 * @returns React Query mutation for creating stock entry
 */
export function useCreateStockEntry(options?: UseCreateStockEntryOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const navigateOnSuccess = options?.navigateOnSuccess ?? true;

  return useMutation<StockEntry, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return erp.stockEntries.create(data);
    },
    onSuccess: (data) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ["stock-entries", "list"] });

      // Invalidate per-item stock caches when itemCode is provided
      if (options?.itemCode) {
        queryClient.invalidateQueries({
          queryKey: ["stock", "bin", options.itemCode],
        });
        queryClient.invalidateQueries({
          queryKey: ["doc-dashboard", "Item", options.itemCode],
        });
      }

      if (navigateOnSuccess) {
        // Redirect to detail page (existing behavior)
        if (data.name) {
          navigate(routes.stockEntry.detail(data.name));
        }
      } else {
        // Inline mode — call custom callback, no navigation
        options?.onSuccess?.(data);
      }
    },
  });
}
